import { Box } from '@mui/material';
import type { PlanNodeStatus } from '@timelog/pm-shared';
import { useTranslation } from 'react-i18next';

/**
 * BR-17 vocabulary with the design's pill styling per status. `label` is the
 * English name, kept for callers; the UI renders `pmPlanning:status.<status>`.
 */
export const STATUS_STYLES: Record<
  PlanNodeStatus,
  { label: string; bg: string; fg: string; dot: string }
> = {
  planned: {
    label: 'Planned',
    bg: 'var(--bg-sunken)',
    fg: 'var(--fg-2)',
    dot: 'var(--color-fog-60)',
  },
  active: {
    label: 'In progress',
    bg: 'var(--color-blue-10)',
    fg: 'var(--color-blue-100)',
    dot: 'var(--color-blue-80)',
  },
  done: {
    label: 'Done',
    bg: 'var(--color-success-10)',
    fg: 'var(--color-success-100)',
    dot: 'var(--color-success-80)',
  },
};

export type StatusPillProps = {
  status: PlanNodeStatus;
  size?: 'small' | 'medium';
};

export const StatusPill = ({ status, size = 'medium' }: StatusPillProps) => {
  const { t } = useTranslation('pmPlanning');
  const style = STATUS_STYLES[status];
  return (
    <Box
      component="span"
      data-automation-id="status-pill"
      data-status={status}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: size === 'medium' ? '9px' : '8px',
        py: size === 'medium' ? '3px' : '2px',
        borderRadius: 'var(--border-radius-pill)',
        bgcolor: style.bg,
        fontSize: 11,
        fontWeight: 500,
        color: style.fg,
        whiteSpace: 'nowrap',
      }}
    >
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: style.dot,
          flex: 'none',
        }}
      />
      {t(`status.${status}`)}
    </Box>
  );
};
